<template>
  <!--
    Renders ONE form question by its type. The six supported types match the
    server (date, number, short_text, long_text, single_choice, multi_choice).
    The renderer is generic — it draws whatever the form definition specifies —
    so new forms need no new field code. Mobile-first: full-width inputs.
  -->
  <div class="question-field">
    <!-- date: HTML date input yields YYYY-MM-DD, matching the server contract. -->
    <v-text-field
      v-if="question.type === 'date'"
      :model-value="modelValue"
      type="date"
      :label="label"
      :error-messages="error ? [error] : []"
      variant="outlined"
      @update:model-value="emitValue"
    />

    <v-text-field
      v-else-if="question.type === 'number'"
      :model-value="modelValue"
      type="number"
      inputmode="decimal"
      :label="label"
      :error-messages="error ? [error] : []"
      variant="outlined"
      @update:model-value="emitValue"
    />

    <v-text-field
      v-else-if="question.type === 'short_text'"
      :model-value="modelValue"
      :label="label"
      :error-messages="error ? [error] : []"
      variant="outlined"
      @update:model-value="emitValue"
    />

    <v-textarea
      v-else-if="question.type === 'long_text'"
      :model-value="modelValue"
      :label="label"
      :error-messages="error ? [error] : []"
      variant="outlined"
      rows="3"
      auto-grow
      @update:model-value="emitValue"
    />

    <!-- single_choice: radios (options can be long, e.g. tyre ratings). -->
    <div v-else-if="question.type === 'single_choice'" class="question-field__group">
      <div class="question-field__legend">{{ label }}</div>
      <v-radio-group
        :model-value="modelValue"
        :error-messages="error ? [error] : []"
        @update:model-value="emitValue"
      >
        <v-radio
          v-for="(opt, i) in options"
          :key="`${opt}-${i}`"
          :label="opt"
          :value="opt"
        />
      </v-radio-group>
    </div>

    <!-- multi_choice: checkboxes bound to an array. -->
    <div v-else-if="question.type === 'multi_choice'" class="question-field__group">
      <div class="question-field__legend">{{ label }}</div>
      <v-checkbox
        v-for="(opt, i) in options"
        :key="`${opt}-${i}`"
        :model-value="selected"
        :label="opt"
        :value="opt"
        hide-details
        density="comfortable"
        @update:model-value="emitValue"
      />
      <div v-if="error" class="question-field__error">{{ error }}</div>
    </div>

    <!-- Unknown type: fail visibly rather than silently dropping the question. -->
    <v-alert v-else type="warning" variant="tonal" density="compact">
      Unsupported question type: {{ question.type }}
    </v-alert>
  </div>
</template>

<script>
// QuestionField — a single dynamic form input driven by a question definition.
// Stateless: the parent owns the answer value (modelValue) and the error string.
export default {
  name: 'QuestionField',
  props: {
    question: {
      type: Object,
      required: true,
    },
    // The current answer: a string for text/date/single_choice, a number string
    // for number, an array for multi_choice.
    modelValue: {
      type: [String, Number, Array],
      default: null,
    },
    // A per-field validation message to display, or '' / null for none.
    error: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  computed: {
    // Label with a trailing asterisk for required questions.
    label () {
      return this.question.required ? `${this.question.label} *` : this.question.label
    },
    options () {
      return Array.isArray(this.question.options) ? this.question.options : []
    },
    // multi_choice value is always an array for the checkbox group.
    selected () {
      return Array.isArray(this.modelValue) ? this.modelValue : []
    },
  },
  methods: {
    emitValue (value) {
      this.$emit('update:modelValue', value)
    },
  },
}
</script>

<style scoped>
.question-field {
  margin-bottom: 8px;
}

.question-field__legend {
  font-size: 1rem;
  font-weight: 600;
  color: #41281b;
  margin-bottom: 4px;
}

.question-field__error {
  color: rgb(var(--v-theme-error));
  font-size: 0.8125rem;
  margin-top: 4px;
}
</style>
